import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog';
import { ScrollArea } from '../../ui/scroll-area';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Card, CardContent } from '../../ui/card';
import { 
  Heart, 
  User, 
  Target, 
  Compass, 
  Sparkles,
  BookOpen,
  Download,
  Cloud,
  CloudOff,
  Mail,
  Check,
  Loader2,
  RefreshCw,
  Lock
} from 'lucide-react';

const ProfileField = ({ label, placeholder, value, onChange, icon: Icon }) => (
  <div className="space-y-2">
    <label className="flex items-center gap-2 text-sm font-semibold text-[var(--primary)]">
      {Icon && <Icon className="w-4 h-4 text-[var(--secondary)]" />}
      {label}
    </label>
    <Textarea
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="min-h-[80px] text-sm resize-none border-[#E6E2D6] focus:border-[var(--secondary)] focus:ring-[var(--secondary)]/20"
    />
  </div>
);

const SelfDiscoveryProfileModal = ({ 
  isOpen, 
  onClose, 
  profile, 
  onUpdateProfile,
  onExportProfile,
  dailyNotes,
  bookmarkedDays,
  retreatStartDate,
  onSyncComplete,
  openSyncPanel: shouldOpenSyncPanel
}) => {
  const [syncEmail, setSyncEmail] = useState('');
  const [syncPin, setSyncPin] = useState('');
  const [syncPinHint, setSyncPinHint] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null); // 'saved', 'loaded', 'error'
  const [syncError, setSyncError] = useState('');
  const [showSyncPanel, setShowSyncPanel] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [hintText, setHintText] = useState('');
  const [isLoadingHint, setIsLoadingHint] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Open sync panel when requested from parent
  React.useEffect(() => {
    if (shouldOpenSyncPanel && isOpen) {
      setShowSyncPanel(true);
    }
  }, [shouldOpenSyncPanel, isOpen]);

  const handleGetHint = async () => {
    if (!syncEmail.trim()) {
      setSyncError('Please enter your email to get hint');
      return;
    }
    
    setIsLoadingHint(true);
    try {
      const response = await fetch(`${API_URL}/api/profile/hint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: syncEmail.trim() })
      });
      
      const data = await response.json();
      
      if (data.success && data.found) {
        setHintText(data.hint);
        setShowHint(true);
      } else {
        setSyncError('No profile found for this email');
      }
    } catch (error) {
      setSyncError('Could not retrieve hint. Please try again.');
    } finally {
      setIsLoadingHint(false);
    }
  };

  const handleSaveToCloud = async () => {
    if (!syncEmail.trim()) {
      setSyncError('Please enter your email address');
      return;
    }
    if (!syncPin || syncPin.length !== 4 || !/^\d{4}$/.test(syncPin)) {
      setSyncError('Please enter a 4-digit PIN');
      return;
    }
    
    setIsSyncing(true);
    setSyncStatus(null);
    setSyncError('');
    
    try {
      const response = await fetch(`${API_URL}/api/profile/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: syncEmail.trim(),
          pin: syncPin,
          pinHint: syncPinHint.trim(),
          profile: profile,
          notes: dailyNotes || {},
          bookmarks: bookmarkedDays || {},
          retreatStartDate: retreatStartDate || ''
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSyncStatus('saved');
        localStorage.setItem('dmu-rimp-retreat-sync-email', syncEmail.trim());
        // Auto-close sync panel after 2 seconds on success
        setTimeout(() => {
          setShowSyncPanel(false);
          setSyncStatus(null);
        }, 2000);
      } else {
        setSyncStatus('error');
        setSyncError(data.detail || 'Failed to save. Please try again.');
      }
    } catch (error) {
      console.error('Sync error:', error);
      setSyncStatus('error');
      setSyncError('Connection error. Please check your internet.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleLoadFromCloud = async () => {
    if (!syncEmail.trim()) {
      setSyncError('Please enter your email address');
      return;
    }
    if (!syncPin || syncPin.length !== 4 || !/^\d{4}$/.test(syncPin)) {
      setSyncError('Please enter your 4-digit PIN');
      return;
    }
    
    setIsSyncing(true);
    setSyncStatus(null);
    setSyncError('');
    
    try {
      const response = await fetch(`${API_URL}/api/profile/load`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: syncEmail.trim(),
          pin: syncPin 
        })
      });
      
      // Read the response text first, then parse
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        setSyncStatus('error');
        setSyncError('Failed to load. Server error.');
        return;
      }
      
      // Handle non-OK responses first (like 403 Incorrect PIN)
      if (!response.ok) {
        setSyncStatus('error');
        setSyncError(data.detail || 'Failed to load. Please check your PIN.');
        return;
      }
      
      if (data.success && data.found) {
        // Update all the data
        if (data.data.profile) {
          Object.keys(data.data.profile).forEach(key => {
            if (data.data.profile[key]) {
              onUpdateProfile(key, data.data.profile[key]);
            }
          });
        }
        
        // Call sync complete callback to update notes/bookmarks in parent
        if (onSyncComplete) {
          onSyncComplete(data.data);
        }
        
        localStorage.setItem('dmu-rimp-retreat-sync-email', syncEmail.trim());
        setSyncStatus('loaded');
      } else if (data.success && !data.found) {
        setSyncError('No profile found for this email. Save your profile first to create one.');
        setSyncStatus(null);
      } else {
        setSyncStatus('error');
        setSyncError(data.detail || 'Failed to load. Please check your PIN.');
      }
    } catch (error) {
      console.error('Load error:', error);
      setSyncStatus('error');
      // Check if it's a network error vs other error
      if (error.message && error.message.includes('body')) {
        setSyncError('Failed to load. Please check your PIN.');
      } else {
        setSyncError('Connection error. Please check your internet.');
      }
    } finally {
      setIsSyncing(false);
    }
  };

  // Load saved email on mount
  React.useEffect(() => {
    const savedEmail = localStorage.getItem('dmu-rimp-retreat-sync-email');
    if (savedEmail) {
      setSyncEmail(savedEmail);
    }
  }, []);

  const profileFields = [
    {
      section: "Relationship (R)",
      icon: Heart,
      color: "bg-[var(--primary)]",
      fields: [
        {
          key: "prayerStyles",
          label: "My Prayer Styles",
          placeholder: "What forms of prayer draw you closest to God? (e.g., Lectio Divina, Rosary, Contemplative Prayer, Adoration, Journaling, Walking Prayer...)"
        },
        {
          key: "spiritualGifts",
          label: "My Spiritual Gifts",
          placeholder: "What spiritual gifts have you identified? (e.g., from Romans 12, 1 Corinthians 12, Ephesians 4)"
        }
      ]
    },
    {
      section: "Identity (I)",
      icon: User,
      color: "bg-[var(--secondary)]",
      fields: [
        {
          key: "temperament",
          label: "My Temperament",
          placeholder: "Choleric, Sanguine, Melancholic, or Phlegmatic? What have you learned about your temperament?"
        },
        {
          key: "personality",
          label: "My Personality Profile",
          placeholder: "What have you learned from Myers-Briggs, Enneagram, DISC, CliftonStrengths, or other assessments?"
        },
        {
          key: "learningStyle",
          label: "My Learning Style",
          placeholder: "Visual, auditory, kinesthetic, or reading/writing? How do you process information best?"
        },
        {
          key: "naturalTalents",
          label: "My Natural Talents",
          placeholder: "What comes easily to you? What do people consistently ask you for help with?"
        }
      ]
    },
    {
      section: "Mission (M)",
      icon: Target,
      color: "bg-[#1B93A4]",
      fields: [
        {
          key: "missionField",
          label: "My Mission Field",
          placeholder: "Mercy, Truth, Justice, or Gathering? Where has God placed you to serve?"
        },
        {
          key: "professionalCharism",
          label: "My Professional Charism",
          placeholder: "How might your career, education, and experience be used for God's kingdom?"
        },
        {
          key: "passionAbilityNeed",
          label: "Passion + Ability + Need",
          placeholder: "Where do your passions meet your abilities and the world's needs?"
        }
      ]
    },
    {
      section: "Purpose (P)",
      icon: Compass,
      color: "bg-[var(--primary)]",
      fields: [
        {
          key: "lifeVerse",
          label: "My Life Verse / Mission Statement",
          placeholder: "Is there a Scripture passage that captures your life's purpose? What is your personal mission statement?"
        },
        {
          key: "patronSaints",
          label: "My Patron Saints & Role Models",
          placeholder: "Which saints inspire you? What qualities do they embody that you want to cultivate?"
        },
        {
          key: "legacyStatement",
          label: "My Legacy Statement",
          placeholder: "At the end of your life, what do you want to be remembered for?"
        }
      ]
    }
  ];

  const hasContent = profile && Object.values(profile).some(v => v && v.trim && v.trim().length > 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="flex items-center gap-2 text-[var(--primary)] font-heading text-2xl">
            <Sparkles className="w-6 h-6 text-[var(--secondary)]" />
            My Self-Discovery Profile
          </DialogTitle>
          <DialogDescription className="text-[#1A1A1A]/60">
            Capture insights about your unique God-given design as you journey through the retreat.
          </DialogDescription>
        </DialogHeader>
        
        {/* Cloud Sync Panel */}
        <div className="px-6 py-3 bg-[#F2F0E9] border-y border-[#E6E2D6]">
          <button
            onClick={() => setShowSyncPanel(!showSyncPanel)}
            className="flex items-center gap-2 text-sm text-[var(--primary)] font-medium w-full"
          >
            <Cloud className="w-4 h-4 text-[#1B93A4]" />
            Cloud Sync - Access your profile from any device
            <span className="ml-auto text-xs text-[#1A1A1A]/50">
              {showSyncPanel ? '▲' : '▼'}
            </span>
          </button>
          
          {showSyncPanel && (
            <div className="mt-3 p-4 bg-white rounded-lg border border-[#E6E2D6]">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-[#1A1A1A]/60 mb-1 block">Your Email</label>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#1A1A1A]/40" />
                      <input
                        type="email"
                        value={syncEmail}
                        onChange={(e) => { setSyncEmail(e.target.value); setSyncError(''); }}
                        placeholder="your@email.com"
                        className="flex-1 px-3 py-2 text-sm border border-[#E6E2D6] rounded-lg focus:border-[#1B93A4] focus:outline-none"
                        data-testid="sync-email-input"
                      />
                    </div>
                  </div>
                  <div className="w-full sm:w-32">
                    <label className="text-xs text-[#1A1A1A]/60 mb-1 block">4-Digit PIN</label>
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-[#1A1A1A]/40" />
                      <input
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        value={syncPin}
                        onChange={(e) => { 
                          const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setSyncPin(val); 
                          setSyncError(''); 
                        }}
                        placeholder="••••"
                        className="flex-1 px-3 py-2 text-sm border border-[#E6E2D6] rounded-lg focus:border-[#1B93A4] focus:outline-none text-center tracking-widest"
                        data-testid="sync-pin-input"
                      />
                    </div>
                  </div>
                </div>
                
                {/* PIN Hint Input */}
                <div>
                  <label className="text-xs text-[#1A1A1A]/60 mb-1 block">PIN Hint (optional) - helps you remember</label>
                  <input
                    type="text"
                    value={syncPinHint}
                    onChange={(e) => setSyncPinHint(e.target.value)}
                    placeholder="e.g., My patron saint's feast month"
                    className="w-full px-3 py-2 text-sm border border-[#E6E2D6] rounded-lg focus:border-[#1B93A4] focus:outline-none"
                    data-testid="sync-hint-input"
                  />
                </div>
                
                <div className="flex gap-2 justify-between items-center">
                  <Button
                    onClick={handleGetHint}
                    disabled={isLoadingHint || !syncEmail.trim()}
                    size="sm"
                    variant="ghost"
                    className="text-[var(--primary)] hover:bg-[var(--primary)]/10 text-xs"
                    data-testid="show-hint-btn"
                  >
                    {isLoadingHint ? (
                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <span>🤔 Forgot PIN?</span>
                    )}
                  </Button>
                  <div className="flex gap-2">
                  <Button
                    onClick={handleSaveToCloud}
                    disabled={isSyncing || !syncEmail.trim() || syncPin.length !== 4}
                    size="sm"
                    className="bg-[#1B93A4] hover:bg-[#1B93A4]/90 text-white"
                    data-testid="cloud-save-btn"
                  >
                    {isSyncing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Cloud className="w-4 h-4 mr-1" />
                        Save
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleLoadFromCloud}
                    disabled={isSyncing || !syncEmail.trim() || syncPin.length !== 4}
                    size="sm"
                    variant="outline"
                    className="border-[#1B93A4] text-[#1B93A4]"
                    data-testid="cloud-load-btn"
                  >
                    {isSyncing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Load
                      </>
                    )}
                  </Button>
                </div>
                </div>
              </div>
              
              {/* Show Hint Display */}
              {showHint && hintText && (
                <div className="mt-3 p-3 rounded-lg bg-purple-50 border border-purple-200">
                  <div className="flex items-start gap-2">
                    <span className="text-lg">💡</span>
                    <div>
                      <p className="text-xs text-purple-600 font-medium">Your PIN Hint:</p>
                      <p className="text-sm text-purple-800">{hintText}</p>
                    </div>
                    <button 
                      onClick={() => setShowHint(false)}
                      className="ml-auto text-purple-400 hover:text-purple-600"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}
              
              {syncError && (
                <div className="mt-3 p-2 rounded-lg text-sm flex items-center gap-2 bg-amber-50 text-amber-700">
                  <CloudOff className="w-4 h-4" /> {syncError}
                </div>
              )}
              
              {syncStatus && !syncError && (
                <div className={`mt-3 p-2 rounded-lg text-sm flex items-center gap-2 ${
                  syncStatus === 'saved' ? 'bg-green-50 text-green-700' :
                  syncStatus === 'loaded' ? 'bg-blue-50 text-blue-700' :
                  'bg-red-50 text-red-700'
                }`}>
                  {syncStatus === 'saved' && <><Check className="w-4 h-4" /> Profile saved to cloud!</>}
                  {syncStatus === 'loaded' && <><Check className="w-4 h-4" /> Profile loaded from cloud!</>}
                  {syncStatus === 'error' && <><CloudOff className="w-4 h-4" /> Sync failed. Please try again.</>}
                </div>
              )}
              
              <p className="text-xs text-[#1A1A1A]/50 mt-3">
                🔐 Your PIN protects your data. <strong>Remember it</strong> — you'll need it to access your profile from other devices.
              </p>
              
              {/* Done button to close sync panel */}
              <button
                onClick={() => setShowSyncPanel(false)}
                className="w-full mt-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
        
        <ScrollArea className="max-h-[50vh] px-6">
          <div className="space-y-6 pb-6">
            {profileFields.map((section) => {
              const SectionIcon = section.icon;
              return (
                <Card key={section.section} className="border-l-4" style={{ borderLeftColor: section.color.includes('5D1F2F') ? 'var(--primary)' : section.color.includes('C5A059') ? 'var(--secondary)' : '#1B93A4' }}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-4">
                      <div className={`w-8 h-8 rounded-full ${section.color} flex items-center justify-center`}>
                        <SectionIcon className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="font-heading font-semibold text-[var(--primary)]">{section.section}</h3>
                    </div>
                    <div className="space-y-4">
                      {section.fields.map((field) => (
                        <ProfileField
                          key={field.key}
                          label={field.label}
                          placeholder={field.placeholder}
                          value={profile[field.key] || ''}
                          onChange={(value) => onUpdateProfile(field.key, value)}
                          icon={BookOpen}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </ScrollArea>
        
        <div className="p-6 pt-4 border-t border-[#E6E2D6] flex justify-between items-center">
          <p className="text-xs text-[#1A1A1A]/40">
            Auto-saved locally • Use Cloud Sync to access anywhere
          </p>
          <div className="flex gap-2">
            {hasContent && onExportProfile && (
              <Button 
                variant="outline" 
                onClick={onExportProfile}
                className="text-[#1B93A4] border-[#1B93A4] hover:bg-[#1B93A4]/10"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Profile
              </Button>
            )}
            <Button 
              onClick={onClose}
              className="bg-[var(--primary)] hover:bg-[var(--primary)]/90"
            >
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelfDiscoveryProfileModal;
